import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Loader2, X } from "lucide-react";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clear } = useCart();
  const [state, setState] = useState("checking"); // checking | paid | timeout | error
  const [orderNumber, setOrderNumber] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!sessionId) {
      setState("error");
      return;
    }
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setOrderNumber(data.order_number);
        if (data.payment_status === "paid") {
          clear();
          setState("paid");
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setState("error");
          return;
        }
      } catch {
        setState("error");
        return;
      }
      attempts.current += 1;
      if (attempts.current >= 8) {
        setState("timeout");
        return;
      }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="pt-40 pb-40 max-w-xl mx-auto text-center px-5 min-h-[60vh]">
      {state === "checking" && (
        <div data-testid="payment-checking">
          <Loader2 size={40} className="mx-auto text-roast animate-spin" />
          <h1 className="font-display text-4xl tracking-tight mt-8">Confirmăm plata...</h1>
          <p className="text-muted-foreground mt-3">Te rugăm să aștepți câteva momente.</p>
        </div>
      )}
      {state === "paid" && (
        <div data-testid="payment-success">
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 200, damping: 15 }} className="h-20 w-20 mx-auto grid place-items-center rounded-full bg-roast text-cream">
            <Check size={36} />
          </motion.div>
          <h1 className="font-display text-5xl tracking-tight mt-8">Plată reușită!</h1>
          <p className="text-muted-foreground mt-4">
            Îți mulțumim! Comanda <span className="text-roast font-semibold">{orderNumber}</span> a fost plătită și înregistrată.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-10">
            <Link to="/cont" className="bg-ink text-cream px-8 py-4 hover:bg-roast transition-colors text-sm font-semibold">Vezi comenzile</Link>
            <Link to="/produse" className="border border-line px-8 py-4 hover:border-ink transition-colors text-sm font-semibold">Continuă cumpărăturile</Link>
          </div>
        </div>
      )}
      {(state === "timeout" || state === "error") && (
        <div data-testid="payment-pending">
          <h1 className="font-display text-4xl tracking-tight mt-8">Verificăm plata</h1>
          <p className="text-muted-foreground mt-4">
            {orderNumber ? `Comanda ${orderNumber} a fost înregistrată. ` : ""}
            Dacă ai finalizat plata, statusul se va actualiza în scurt timp în contul tău.
          </p>
          <Link to="/cont" className="inline-block mt-8 bg-ink text-cream px-8 py-4 hover:bg-roast transition-colors text-sm font-semibold">Mergi la contul meu</Link>
        </div>
      )}
    </div>
  );
}
